import { WheelJoint, type Body } from "fast-2d";
import { Camera } from "../render/Camera";
import type { Demo, Scene } from "./types";
import { makeWorld, floor, walls, box, ball } from "./helpers";

/**
 * A drivable buggy built from a chassis and two wheels joined by `WheelJoint`s —
 * each wheel rides a damped suspension spring, is held against sideways slip, and
 * is spun by an angular motor. It powers itself back and forth over bumps and
 * ramps; grab the chassis to flip it, toss it, or hold it back.
 */
export const vehicle: Demo = {
	id: "vehicle",
	title: "Vehicle",
	blurb:
		"A buggy made of two WheelJoints: spring-loaded suspension, no-slip wheels, and motor-driven drive. It climbs bumps and ramps on its own — grab it to flip or fling it.",
	category: "Joints & Machines",
	build(): Scene {
		const world = makeWorld();
		floor(world, 40);
		walls(world, 16, 16);

		// Bumpy terrain: a scatter of static rollers and a couple of ramps.
		for (let i = -12; i <= 12; i += 3) {
			ball(world, i + 1.5, 0.1, 0.55, { isStatic: true, friction: 0.9 });
		}
		box(world, -8, -0.4, 4, 0.4, { isStatic: true, angle: -0.32, friction: 0.9 });
		box(world, 8, -0.4, 4, 0.4, { isStatic: true, angle: 0.32, friction: 0.9 });

		// The buggy: a chassis with a wheel mounted at each end.
		const cx = 0;
		const cy = -3;
		const chassis = box(world, cx, cy, 2.8, 0.5, { density: 1, friction: 0.6 });
		const mountY = 0.55; // wheels sit just below the chassis (screen-down = +y)
		const halfBase = 1.0;
		const wheelR = 0.55;

		const makeWheel = (sign: number): WheelJoint => {
			const wheel = ball(world, cx + sign * halfBase, cy + mountY, wheelR, {
				density: 1,
				friction: 0.95,
			});
			const joint = new WheelJoint(chassis, wheel, {
				localAnchorA: [sign * halfBase, mountY],
				localAnchorB: [0, 0],
				localAxisA: [0, 1],
				frequency: 4,
				dampingRatio: 0.7,
				enableMotor: true,
				motorSpeed: 0,
				maxMotorTorque: 55,
			});
			world.addConstraint(joint);
			return joint;
		};

		const wheels = [makeWheel(-1), makeWheel(1)];

		// Self-drive: roll one way until near a wall, then reverse.
		const drive = 16;
		let dir = 1;
		const onBeforeStep = (): void => {
			if (chassis.position.x > 11) dir = -1;
			else if (chassis.position.x < -11) dir = 1;
			// +y-down world: a positive motor speed rolls the wheels so the car
			// travels in +x, matching `dir`.
			const speed = dir * drive;
			for (const w of wheels) w.motorSpeed = speed;
			(chassis as Body).wake();
		};

		return {
			world,
			camera: new Camera(0, -4, 19),
			onBeforeStep,
			hint: "The buggy drives itself — grab the chassis to flip or fling it",
		};
	},
};
